import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Divider,
  FormGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onlineTourPropertySearchFilterType } from '../api';
import { SelectFilter } from '../components/SelectFilter';
import { IOnlineTourSearchPropertyResult } from '../utils/types';
import { useLocation } from 'react-router-dom';

export type FilterValues = {
  categories: number[];
  tourDayCount: number[];
  tourConcept: number[];
  departureCities: number[];
  departureAirports: number[];
  accCities: number[];
  months: number[];
  startDate: Date | null;
  endDate: Date | null;
};

type OnlineToursFiltersProps = {
  initialFilterValues: FilterValues;
  onSearch: (filterValues: FilterValues) => void;
  control: boolean;
  setControl: (control: boolean) => void;
};

const months = [
  { id: 1, searchText: 'Ocak' },
  { id: 2, searchText: 'Şubat' },
  { id: 3, searchText: 'Mart' },
  { id: 4, searchText: 'Nisan' },
  { id: 5, searchText: 'Mayıs' },
  { id: 6, searchText: 'Haziran' },
  { id: 7, searchText: 'Temmuz' },
  { id: 8, searchText: 'Ağustos' },
  { id: 9, searchText: 'Eylül' },
  { id: 10, searchText: 'Ekim' },
  { id: 11, searchText: 'Kasım' },
  { id: 12, searchText: 'Aralık' },
];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function OnlineToursFilters({
  initialFilterValues,
  onSearch,
  control,
  setControl,
}: OnlineToursFiltersProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const query = useQuery();

  const [filterValues, setFilterValues] =
    useState<FilterValues>(initialFilterValues);
  const [tourCategories, setTourCategoriesN] = useState<
    IOnlineTourSearchPropertyResult[]
  >([]);
  const [tourDayCount, setTourDayCount] = useState<
    IOnlineTourSearchPropertyResult[]
  >([]);
  const [tourConcept, setTourConcept] = useState<
    IOnlineTourSearchPropertyResult[]
  >([]);
  const [departureCities, setDepartureCities] = useState<
    IOnlineTourSearchPropertyResult[]
  >([]);

  async function fetchOptions(searchTerm: string, type: string) {
    const res = await onlineTourPropertySearchFilterType(searchTerm, type);
    if (res.status === 200) {
      const data: IOnlineTourSearchPropertyResult[] = await res.json();
      const sortedData = data.sort((a, b) => {
        if (a.searchText < b.searchText) return -1;
        if (a.searchText > b.searchText) return 1;
        return 0;
      });
      return sortedData;
    } else {
      throw new Error(t('onlineToursFilters.failedFetchData'));
    }
  }

  async function fetchCategories(searchTerm: string) {
    const data = await fetchOptions(searchTerm, 'CATEGORY');
    setTourCategoriesN(data);
  }

  async function fetchTourDayCount(searchTerm: string) {
    const data = await fetchOptions(searchTerm, 'TOUR_DAY_COUNT');
    const sortedData = data.sort((a, b) => {
      const numA = parseInt(a.searchText, 10);
      const numB = parseInt(b.searchText, 10);
      return numA - numB;
    });
    setTourDayCount(sortedData);
  }

  async function fetchTourConcept(searchTerm: string) {
    const data = await fetchOptions(searchTerm, 'TOUR_CONCEPT');
    setTourConcept(data);
  }

  async function fetchDepartureCities(searchTerm: string) {
    const data = await fetchOptions(searchTerm, 'DEPARTURE_CITIES');
    setDepartureCities(data);
  }

  useEffect(() => {
    if (!control) return;
    const categories = query.get('categories')?.split(',').map(Number) || [];
    const tourDayCount =
      query.get('tourDayCount')?.split(',').map(Number) || [];
    const tourConcept = query.get('tourConcept')?.split(',').map(Number) || [];
    const departureCities =
      query.get('departureCities')?.split(',').map(Number) || [];
    const startDate = query.get('startDate')
      ? new Date(query.get('startDate')!)
      : null;
    const endDate = query.get('endDate')
      ? new Date(query.get('endDate')!)
      : null;
    const months = query.get('months')?.split(',').map(Number) || [];

    setFilterValues((prevValues) => ({
      ...prevValues, // Merge previous state
      categories,
      tourDayCount,
      tourConcept,
      departureCities,
      startDate,
      endDate,
      months,
    }));
    setControl(false);
  }, [query]);

  function handleFilterValueChange(
    key: keyof typeof filterValues,
    value: unknown,
  ) {
    setFilterValues({ ...filterValues, [key]: value });
  }

  function handleSearch() {
    onSearch(filterValues);
  }

  return (
    <Fragment>
      <FilterItem title={t('onlineToursFilters.tourCategories')}>
        <SelectFilter
          values={filterValues.categories}
          options={tourCategories.map((place) => ({
            id: place.id,
            label: place.searchText,
          }))}
          onChange={(values) => handleFilterValueChange('categories', values)}
          onSearch={(searchTerm) => fetchCategories(searchTerm)}
          enableSearch
          manualSearch
        />
      </FilterItem>
      <Divider />
      <FilterItem title={t('onlineToursFilters.tourStartDate')}>
        <DatePicker
          value={filterValues.startDate}
          onChange={(newValue) =>
            handleFilterValueChange('startDate', newValue)
          }
          slotProps={{
            field: {},
            textField: {
              size: 'small',
              placeholder: '10.01.2024',
            },
          }}
          sx={{ width: '100%' }}
          format="dd.MM.yyyy"
        />
        <DatePicker
          value={filterValues.endDate}
          onChange={(newValue) => handleFilterValueChange('endDate', newValue)}
          slotProps={{
            field: {},
            textField: {
              size: 'small',
              placeholder: '10.12.2024',
            },
          }}
          sx={{
            width: '100%',
            mt: 1,
          }}
          format="dd.MM.yyyy"
        />
      </FilterItem>
      <Divider />
      <FilterItem title={t('onlineToursFilters.months')}>
        <SelectFilter
          values={filterValues.months}
          options={months.map((m) => ({
            id: m.id,
            label: m.searchText,
          }))}
          onChange={(values) => handleFilterValueChange('months', values)}
        />
      </FilterItem>
      <Divider />
      <FilterItem
        title={t('onlineToursFilters.startingPoint')}
        onExpand={() =>
          departureCities.length === 0 && fetchDepartureCities('')
        }
      >
        <SelectFilter
          values={filterValues.departureCities}
          options={departureCities.map((place) => ({
            id: place.id,
            label: place.searchText,
          }))}
          onChange={(values) =>
            handleFilterValueChange('departureCities', values)
          }
          onSearch={(searchTerm) => fetchDepartureCities(searchTerm)}
          enableSearch
          manualSearch
        />
      </FilterItem>
      <Divider />
      <FilterItem
        title={t('onlineToursFilters.tourDuration')}
        onExpand={() => tourDayCount.length === 0 && fetchTourDayCount('')}
      >
        <FormGroup>
          <SelectFilter
            values={filterValues.tourDayCount}
            options={tourDayCount.map((place) => ({
              id: place.id,
              label: place.searchText,
            }))}
            onChange={(values) =>
              handleFilterValueChange('tourDayCount', values)
            }
          />
        </FormGroup>
      </FilterItem>
      <Divider />
      <FilterItem
        title={t('onlineToursFilters.tourConcept')}
        onExpand={() => tourConcept.length === 0 && fetchTourConcept('')}
      >
        <FormGroup>
          <SelectFilter
            values={filterValues.tourConcept}
            options={tourConcept.map((place) => ({
              id: place.id,
              label: place.searchText,
            }))}
            onChange={(values) =>
              handleFilterValueChange('tourConcept', values)
            }
          />
        </FormGroup>
      </FilterItem>
      <Box
        sx={{
          position: 'sticky',
          bottom: isMobile ? '-40px' : 0,
          backgroundColor: 'white',
        }}
      >
        <Button
          fullWidth
          onClick={handleSearch}
          variant="contained"
          color="secondary"
          sx={{ mt: 2, mb: 3 }}
        >
          {t('onlineToursFilters.searchNow')}
        </Button>
      </Box>
    </Fragment>
  );
}

function FilterItem(props: {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  onExpand?: () => void;
}) {
  return (
    <Accordion
      defaultExpanded={props.defaultExpanded}
      sx={{
        border: 'none',
        boxShadow: 'none',
        py: 0.75,
        '&.MuiAccordion-root': {
          '&:before': {
            display: 'none',
          },
        },
      }}
      onChange={(e, exp) => exp && props.onExpand && props.onExpand()}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon color="secondary" />}
        aria-controls="panel1-content"
      >
        <Box
          sx={{
            fontSize: '18px',
          }}
        >
          {props.title}
        </Box>
      </AccordionSummary>
      <AccordionDetails>{props.children}</AccordionDetails>
    </Accordion>
  );
}
